import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell } from 'recharts';
import { formatearNumeros } from 'utils/utilitario';
import useStyles from './OutageDashboardStyles';
import {
  ANGULO_INICIO_GRAFICO,
  ANGULO_SEPARACION_GRAFICO,
  ANCHO_TRAZO_GRAFICO,
  CENTRO_GRAFICO,
  CLAVE_DATO_GRAFICO,
  OFFSET_FIN_DEGRADADO,
  OFFSET_INICIO_DEGRADADO,
  PREFIJO_GRADIENTE_GRAFICO,
  RADIO_ESQUINA_GRAFICO,
  RADIO_EXTERNO_GRAFICO,
  RADIO_INTERNO_GRAFICO,
  TAMANO_GRAFICO,
} from './outageDashboard.constants';
import { ColorGradiente, SectorGrafico } from './outageDashboard.helpers';

/**
 * Normaliza cantidad de clientes a número finito o null.
 * @param valor cantidad cruda del dashboard
 * @returns número usable o null
 */
const aNumeroClientes = (valor: number | string | null): number | null => {
  if (typeof valor === 'number') {
    return Number.isFinite(valor) ? valor : null;
  }
  if (valor == null) {
    return null;
  }
  const parseado = Number(valor);
  return Number.isFinite(parseado) ? parseado : null;
};

/**
 * Gráfico de clientes sin servicio / recuperados del dashboard de corte.
 * @param clientesSin cantidad sin servicio
 * @param clientesRec cantidad recuperados
 * @param totalAfectados total de afectados
 * @param objetoGrafico sectores del pie
 * @param colores degradados por sector
 * @param testId data-testid de la card
 */
const OutageClientesChart = ({
  clientesSin,
  clientesRec,
  totalAfectados,
  objetoGrafico,
  colores,
  testId,
}: {
  clientesSin: number | string | null;
  clientesRec: number | string | null;
  totalAfectados: number | string | null;
  objetoGrafico: SectorGrafico[];
  colores: ColorGradiente[];
  testId: string;
}) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const sinServicio = aNumeroClientes(clientesSin);
  const recuperados = aNumeroClientes(clientesRec);
  const total = aNumeroClientes(totalAfectados);

  return (
    <div className={styles.rightCol}>
      <div className={styles.chartCard} data-testid={testId}>
        <div className={styles.chartHeader}>
          <p className={styles.chartHeaderText}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.chart.titulo')}</p>
        </div>
        <div className={styles.chartBody}>
          <div>
            <p className={styles.sinServicioNum}>{formatearNumeros(sinServicio)}</p>
            <div className={styles.sinServicioLabel}>
              <span className={styles.dotRojo} />
              <p className={styles.labelSecundario}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.chart.sinServicio')}</p>
            </div>
          </div>
          <div className={styles.chartRelative}>
            <PieChart width={TAMANO_GRAFICO} height={TAMANO_GRAFICO}>
              <defs>
                {objetoGrafico.map((entry, index) => (
                  <linearGradient key={entry.name} id={`${PREFIJO_GRADIENTE_GRAFICO}${index}`}>
                    <stop offset={OFFSET_INICIO_DEGRADADO} stopColor={colores[index % colores.length].start} />
                    <stop offset={OFFSET_FIN_DEGRADADO} stopColor={colores[index % colores.length].end} />
                  </linearGradient>
                ))}
              </defs>
              <Pie
                data={objetoGrafico}
                dataKey={CLAVE_DATO_GRAFICO}
                cx={CENTRO_GRAFICO}
                cy={CENTRO_GRAFICO}
                innerRadius={RADIO_INTERNO_GRAFICO}
                outerRadius={RADIO_EXTERNO_GRAFICO}
                paddingAngle={ANGULO_SEPARACION_GRAFICO}
                startAngle={ANGULO_INICIO_GRAFICO}
                cornerRadius={RADIO_ESQUINA_GRAFICO}
                strokeWidth={ANCHO_TRAZO_GRAFICO}
              >
                {objetoGrafico.map((entry, index) => (
                  <Cell key={entry.name} fill={`url(#${PREFIJO_GRADIENTE_GRAFICO}${index})`} />
                ))}
              </Pie>
            </PieChart>
            <div className={styles.chartCenter}>
              <p className={styles.recuperadosNum}>{formatearNumeros(recuperados)}</p>
              <div className={styles.recuperadosLabel}>
                <div className={styles.rowLabel}>
                  <span className={styles.dotVerde} />
                  <span>{t('desconexion:paginas.tracking.corte.avisoPago.textos.clientes')}</span>
                </div>
                <span>{t('desconexion:paginas.tracking.corte.avisoPago.textos.recuperados')}</span>
              </div>
            </div>
          </div>
          <p className={styles.totalText}>
            {t('desconexion:paginas.tracking.corte.avisoPago.textos.deUnTotalDe')}{' '}
            <span className={styles.totalStrong}>{formatearNumeros(total)}</span>
            <br />
            {t('desconexion:paginas.tracking.corte.avisoPago.textos.clientesAfectadosTotales')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OutageClientesChart;
